import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { WorkshopAppointment } from '@chancenwerk/shared/workshops/entities/WorkshopAppointment.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';

/**
 * @customElement akademie-page-workshop-appointments
 */
@customElement('akademie-page-workshop-appointments')
export class AkademiePageWorkshopAppointments extends CrPage {
  constructor() {
    super();

    this.label = 'Termine';
    this.description = 'Übersicht über alle Termine';
    this.permission = ChancenPermission.akademie.EDIT_WORKSHOP;
  }

  override content() {
    return this._overview();
  }

  private _overview() {
    const options = buildChancenDataViewOptions<typeof WorkshopAppointment>(
      {
        class: WorkshopAppointment,
        itemSpacing: this.spacing.md,
        view: new View({ filter: new AdornisFilter({ sortingFields: ['start'], aspects: [] }) }),
        onlyShowLastTranslation: true,
        customColumns: [
          {
            columnName: '',
            columnWidth: 'max-content',
            customID: 'icon',
            getFieldMapping: w => html`
              <cr-button mode="icon" icon="visibility" href=${`/appointment/${w._id}`}></cr-button>
            `,
          },
          {
            columnName: 'Wann',
            fieldName: 'start',
            columnWidth: 'max-content',
            selectionSet: { start: 1 },
            getFieldMapping: w => w.start?.toFormat('dd.MM.yyyy HH:mm') ?? 'nicht eingetragen',
          },
          {
            columnName: 'Ende',
            fieldName: 'end',
            columnWidth: 'max-content',
            selectionSet: { end: 1 },
            getFieldMapping: w => w.end?.toFormat('HH:mm') ?? 'nicht eingetragen',
          },
          {
            columnName: 'Workshop',
            customID: 'workshop',
            selectionSet: { workshop: { title: 1 } },
            getFieldMapping: w => w.workshop?.title ?? '?',
          },
          {
            columnName: 'Teilnehmer',
            customID: 'attendance',
            selectionSet: { attendance: { _id: 1 } },
            getFieldMapping: w => w.attendance?.length ?? 0,
          },
        ],
      } as DataViewOptions<typeof WorkshopAppointment>,
      { fieldAspectGeneration: { hideFieldsWithoutTranslation: true, whitelist: { start: 1 } } },
    );

    return html`
      <cr-card padding="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })} margin="sm">
        ${FilterEditor({ options: options.filterOptions })}
      </cr-card>
      <cr-data-view ${css({ marginTop: this.spacing.sm })} .options=${options.dataViewOptions}> </cr-data-view>
    `;
  }
}
